import { useAcoes, useEstado, usePode } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { Botao, Rosto } from "../componentes/comuns.jsx";
import { CartaoDosPrimeirosPassos } from "../componentes/PrimeirosPassos.jsx";
import { comecarDemonstracao } from "../demonstracao/Demonstracao.jsx";
import { CartaoDoWhatsapp } from "../componentes/AvisoDoWhatsapp.jsx";

/**
 * Um número da frase do dia, que leva ao recorte de que ele é a contagem.
 *
 * Zero não vira link: mandar alguém para uma lista vazia é a promessa que a tela
 * não cumpre. O plural acompanha o número — "1 prazos" faz a primeira frase da
 * mesa parecer rascunho.
 */
function Numero({ valor, um, varios, para, aoIr }) {
  const texto = `${valor} ${valor === 1 ? um : varios}`;
  if (!valor || !para) return <span>{texto}</span>;
  return (
    <button
      type="button"
      onClick={() => aoIr(para)}
      className="cursor-pointer border-none bg-transparent p-0 font-semibold text-inherit underline decoration-dotted underline-offset-2 hover:decoration-solid"
    >
      {texto}
    </button>
  );
}

/**
 * Nenhuma conversa aberta: o resumo do dia — ou o primeiro passo do escritório.
 *
 * Três estados, e não um. A frase de zeros era dita também durante o
 * carregamento e no erro (`totalDaMesa` nulo virava 0, `avisos` nasce `[]`): a
 * tela afirmava com confiança um número que ainda não tinha. E no escritório
 * recém-criado os três botões eram beco — puxar da fila responde "Nada na fila",
 * a demonstração pede um atendimento aberto que não existe.
 */
export default function BoasVindas() {
  const {
    contadores,
    totalDaMesa,
    mesa,
    avisos,
    usuario,
    equipe,
    mesaCarregando,
    mesaErro,
    tarefasAtrasadas,
    workspace,
    workspaces,
  } = useEstado();
  const { puxarFila, novoCaso, tentarMaisDaMesa } = useAcoes();
  const { ir } = useNavegacao();
  /* Contagem do BANCO, não da página carregada: com 528 casos e 200 por página,
     contar aqui dizia "12 na fila" para quem tinha quarenta. */
  const fila = contadores.Fila ?? 0;
  const abertos = totalDaMesa ?? mesa.length;
  const prazos = avisos.filter((a) => a.titulo.startsWith("Prazo")).length;
  const abrindo = mesaCarregando && totalDaMesa == null;
  /* A saudação era fixa em "Bom dia" — e é a primeira frase da mesa, numa casa
     que atende por WhatsApp até as 20h. */
  const hora = new Date().getHours();
  const saudacao = hora < 12 ? "Bom dia" : hora < 18 ? "Boa tarde" : "Boa noite";
  const vazia = !abrindo && !mesaErro && totalDaMesa === 0;
  /* Montar a equipe e ver a demonstração são o primeiro dia do escritório, ou
     trabalho de quem cuida da equipe. Para quem só atende, eram dois botões na
     primeira tela todos os dias que não levavam a nada que fosse dele. */
  const pode = usePode();
  const mostraConfigurar = equipe.length <= 1 || pode("equipe:ver");
  /* O setor só é dito a quem tem mais de um: é o que impede gravar no setor
     errado sem notar — e para quem só tem um, é ruído na primeira frase. */
  const setorAqui = workspaces.length > 1 ? workspace : null;

  return (
    <div className="flex h-full flex-col items-center justify-center p-8 text-center">
      {/* O avatar de quem está logado, o mesmo do trilho — o monograma "G" era do
          escritório e não dizia para quem a mesa estava falando. */}
      <div className="mb-5 inline-flex rounded-[28px] shadow-[0_18px_40px_color-mix(in_oklab,_var(--color-foreground)_7%,_transparent)]">
        <Rosto pessoa={usuario?.nome} foto={usuario?.avatar} tamanho={88} />
      </div>
      <h2 className="m-0 mb-2 font-serif text-[30px] font-normal">
        {saudacao}, {usuario?.nome ?? ""}.
      </h2>
      {setorAqui ? (
        <p className="m-0 -mt-1 mb-2 text-apoio font-semibold tracking-[.03em] text-muted-foreground uppercase">
          Setor {setorAqui.nome}
        </p>
      ) : null}
      <p className={`m-0 mb-6 max-w-[440px] text-corpo leading-[1.6] ${mesaErro ? "text-primary" : "text-tinta-suave"}`}>
        {abrindo ? (
          "Abrindo a mesa…"
        ) : mesaErro ? (
          mesaErro
        ) : vazia ? (
          "Nenhum atendimento ainda — o primeiro caso entra por aqui."
        ) : (
          /*
           * Um nome só na mesma frase: "atendimento" é o nome da vista e do
           * endereço (`/atendimentos`), então é ele que vale aqui.
           *
           * E os três números LEVAM a algum lugar. Era a primeira frase da mesa
           * contando o dia inteiro em texto morto: quem lia "quatro prazos
           * apertados" ia ao trilho e procurava quais. Cada número abre o recorte
           * de que ele é a contagem.
           */
          <>
            <Numero
              valor={fila}
              um="atendimento na fila"
              varios="atendimentos na fila"
              para="/atendimentos?filtro=Fila"
              aoIr={ir}
            />
            {", "}
            {/* O prazo apertado é o do SINO (`avisos`), e o quadro é onde ele se
                resolve — "atrasadas" é o recorte mais perto de "apertado" que a
                tela de Tarefas tem. Sem tarefa atrasada, o destino é a lista de
                notificações, que é de onde a conta saiu. */}
            <Numero
              valor={prazos}
              um="prazo apertado"
              varios="prazos apertados"
              para={tarefasAtrasadas ? "/tarefas?tarefas=atrasadas" : "/notificacoes"}
              aoIr={ir}
            />
            {" e "}
            <Numero valor={abertos} um="atendimento aberto" varios="atendimentos abertos" para="/atendimentos" aoIr={ir} />.
          </>
        )}
      </p>
      {/*
       * O escritório novo não começa do zero — porque ele não está no zero.
       *
       * "Nenhum atendimento ainda" era a mesa inteira dizendo "você não fez
       * nada", e quem lê isso fecha a aba. Entrar já custou criar o escritório
       * e a conta: isso é passo cumprido e passa a ser contado. O resto da
       * barra é o caminho que sobrou, com o próximo passo escrito — a régua
       * mostra a distância que falta, e distância curta é distância que alguém
       * percorre.
       */}
      {/* O canal ANTES dos atalhos: de nada adianta "Puxar próximo da fila" se o
          WhatsApp está fora — o caso puxado não vai receber resposta. Conectado
          é uma linha discreta; fora é o cartão com o QR dentro. */}
      <CartaoDoWhatsapp />

      {/* Os primeiros passos, do papel de quem lê: some quando o último se cumpre
          (widget que aparece quando precisa). Substituiu a barra "escritório de
          pé", que contava só equipe e primeiro atendimento — e contava a mesma
          coisa para quem configura e para quem atende. */}
      <CartaoDosPrimeirosPassos />

      <div className="flex flex-wrap justify-center gap-2.5">
        {mesaErro ? (
          <Botao
            rotulo="Tentar de novo"
            aoTocar={tentarMaisDaMesa}
            destaque
            estilo="font-size:14px;font-weight:600;padding:12px 20px;border-radius:12px"
          />
        ) : vazia ? (
          /* Mesa vazia: as duas portas que funcionam. Puxar da fila e a
             demonstração pedem caso — e caso é justamente o que não existe. */
          <>
            <Botao
              rotulo="+ Primeiro atendimento"
              aoTocar={novoCaso}
              destaque
              estilo="font-size:14px;font-weight:600;padding:12px 20px;border-radius:12px"
            />
            {/* A demonstração não pede mais caso: sem atendimento ela passeia
                pelas áreas da mesa. O WhatsApp e a equipe saíram daqui — são
                passos do cartão acima, que sabe quando estão cumpridos. */}
            <Botao
              rotulo="Ver a demonstração"
              aoTocar={comecarDemonstracao}
              estilo="font-size:14px;background:transparent;padding:12px 20px;border-radius:12px"
            />
          </>
        ) : abrindo ? null : (
          <>
            <Botao
              rotulo="Puxar próximo da fila"
              aoTocar={puxarFila}
              destaque
              estilo="font-size:14px;font-weight:600;padding:12px 20px;border-radius:12px"
            />
            {/* Quem chega pela primeira vez não sabe onde clicar: aqui é o único
                lugar da mesa onde a pessoa está parada, olhando, sem caso aberto. */}
            {mostraConfigurar ? (
              <Botao
                rotulo="Ver a demonstração"
                aoTocar={comecarDemonstracao}
                estilo="font-size:14px;background:transparent;padding:12px 20px;border-radius:12px"
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
